import { useRoomContext } from "@livekit/components-react";
import { Ear, Volume2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { findPhoneAudio, type PhoneAudio } from "./phone-audio";

type Props = { phoneAudio: PhoneAudio | null; className: string };

/**
 * Loudspeaker / earpiece switch for Android phones (see phone-audio.ts). The call starts on the
 * loudspeaker. Headphones take over while they're connected, and the switch hides.
 */
export const SpeakerToggle = ({ phoneAudio, className }: Props) => {
  const room = useRoomContext();
  const [audio, setAudio] = useState(phoneAudio);
  const [onSpeaker, setOnSpeaker] = useState(true);
  const [switching, setSwitching] = useState(false);
  const headsetRef = useRef(phoneAudio?.headset ?? false);

  useEffect(() => {
    if (!phoneAudio) return;
    const onDeviceChange = async () => {
      const next = await findPhoneAudio();
      if (!next) return;
      setAudio(next);
      if (next.headset === headsetRef.current) return;
      headsetRef.current = next.headset;
      try {
        // "default" lets Chrome pick the headphones; back on the loudspeaker once they're gone.
        await room.switchActiveDevice("audioinput", next.headset ? "default" : next.speakerId);
        if (!next.headset) setOnSpeaker(true);
      } catch {
        // Keep the current route.
      }
    };
    navigator.mediaDevices.addEventListener("devicechange", onDeviceChange);
    return () => navigator.mediaDevices.removeEventListener("devicechange", onDeviceChange);
  }, [phoneAudio, room]);

  if (!audio?.earpieceId || audio.headset) return null;

  const toggle = async () => {
    const next = !onSpeaker;
    const deviceId = next ? audio.speakerId : audio.earpieceId;
    if (!deviceId) return;
    setSwitching(true);
    try {
      await room.switchActiveDevice("audioinput", deviceId);
      setOnSpeaker(next);
    } catch {
      // Keep the current route.
    } finally {
      setSwitching(false);
    }
  };

  return (
    <button
      type="button"
      className={className}
      aria-pressed={onSpeaker}
      disabled={switching}
      onClick={() => void toggle()}
    >
      {onSpeaker ? <Volume2 size={17} aria-hidden="true" /> : <Ear size={17} aria-hidden="true" />}
      {onSpeaker ? "Speaker on" : "Speaker off"}
    </button>
  );
};
